import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useLanguage } from '@/hooks/use-language';
import { useGroups } from '@/hooks/use-groups';
import type { GroupData } from '@/contexts/group/GroupContext';
import { useSupabase } from '@/contexts/supabase';
import { EventData } from '@/contexts/event/EventContext';
import { CalendarIcon, Trash2, Repeat, Eye, Download, Copy, XCircle, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { RecurringEventManager } from './RecurringEventManager';
import { DuplicateEventDialog } from './DuplicateEventDialog';
import { CancelEventDialog } from './CancelEventDialog';
import { exportParticipantsToCsv } from '@/utils/exportParticipants';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface EditEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: EventData | null;
  onEventUpdated: () => void;
  onEventDeleted?: () => void;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function resolveGroupId(raw: string | undefined, allGroups: GroupData[]): string | null {
  if (!raw) return null;
  const match = allGroups.find(group => group.id === raw || group.name === raw);
  if (match) {
    return match.id;
  }
  return UUID_REGEX.test(raw) ? raw : null;
}

export function EditEventDialog({ open, onOpenChange, event, onEventUpdated, onEventDeleted }: EditEventDialogProps) {
  const { t } = useLanguage();
  const supabase = useSupabase();
  const { groups, allGroups } = useGroups();
  const [loading, setLoading] = useState(false);
  const [seriesManagerOpen, setSeriesManagerOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelScope, setCancelScope] = useState<'single' | 'future'>('single');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteScope, setDeleteScope] = useState<'single' | 'future' | 'all'>('single');
  const [editScopeDialogOpen, setEditScopeDialogOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);
  const [masterEvent, setMasterEvent] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventDate: new Date(),
    eventTime: '',
    location: '',
    groupId: '',
    level: '',
    maxParticipants: 20,
    notes: '',
    customSignupHours: undefined as number | undefined,
    editScope: 'single' as 'single' | 'future',
  });

  useEffect(() => {
    if (event) {
      // Find the group ID from groups array if event.group is a name
      const resolvedGroupId = resolveGroupId(event.group, allGroups);

      setFormData({
        title: event.title,
        description: event.description || '',
        eventDate: event.date,
        eventTime: event.time,
        location: event.location,
        groupId: resolvedGroupId || '',
        level: event.level || '',
        maxParticipants: event.maxParticipants,
        notes: event.notes || '',
        customSignupHours: event.customSignupHours,
        editScope: 'single',
      });

      // Fetch master event details if this is part of a series
      if (event.parentEventId) {
        (async () => {
          const { data } = await (supabase as any)
            .from('events')
            .select('*')
            .eq('id', event.parentEventId)
            .single();
          if (data) setMasterEvent(data);
        })();
      }
    }
  }, [event, supabase, groups, allGroups]);

  const handleSave = async () => {
    if (!event) return;
    
    console.log('[EditEventDialog] Saving event with formData:', formData);
    
    // Validate maxParticipants
    if (formData.maxParticipants < 1) {
      console.error('[EditEventDialog] Invalid maxParticipants:', formData.maxParticipants);
      toast.error('Max participants must be at least 1');
      return;
    }
    
    // For recurring events, show confirmation dialog if scope not already confirmed
    if (event.parentEventId && !pendingSave) {
      setPendingSave(true);
      setEditScopeDialogOpen(true);
      return;
    }
    
    await performSave(formData.editScope);
  };

  const performSave = async (editScope: 'single' | 'future') => {
    if (!event) return;
    
    setLoading(true);
    try {
      const resolvedGroupId = resolveGroupId(formData.groupId, allGroups);
      const updates: any = {
        title: formData.title,
        description: formData.description,
        event_date: formData.eventDate.toISOString().split('T')[0],
        event_time: formData.eventTime,
        location: formData.location,
        group_id: resolvedGroupId ?? null,
        level: formData.level || null,
        max_participants: formData.maxParticipants,
        notes: formData.notes || null,
        custom_signup_hours: formData.customSignupHours || null,
        updated_at: new Date().toISOString(),
      };

      console.log('[EditEventDialog] Updates to apply:', updates);

      // Update the event(s)
      let result;
      if (event.parentEventId && editScope === 'future') {
        // Update this event and all future events in the series
        const dateStr = formData.eventDate.toISOString().split('T')[0];
        console.log('[EditEventDialog] Updating future events from:', dateStr);
        result = await (supabase as any)
          .from('events')
          .update(updates)
          .gte('event_date', dateStr)
          .eq('parent_event_id', event.parentEventId);
      } else {
        // Update only this event
        console.log('[EditEventDialog] Updating single event:', event.id);
        result = await (supabase as any)
          .from('events')
          .update(updates)
          .eq('id', event.id);
      }
      
      console.log('[EditEventDialog] Update result:', result);
      
      if (result.error) throw result.error;

      toast.success(t('admin.event_updated'));
      setEditScopeDialogOpen(false);
      setPendingSave(false);
      onEventUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('[EditEventDialog] Error updating event:', error);
      toast.error('Failed to update event: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (scope?: 'single' | 'future' | 'all') => {
    if (!event) return;
    
    const isRecurring = !!event.parentEventId || event.isRecurringMaster;
    
    // For recurring events, if no scope is provided, show dialog to let user choose
    if (isRecurring && !scope) {
      setDeleteScope('single'); // Default selection
      setDeleteConfirmOpen(true);
      return;
    }
    
    // For non-recurring or when scope is explicitly provided
    const actualScope = scope || 'single';
    setDeleteScope(actualScope);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!event) return;
    
    const isRecurring = !!event.parentEventId || event.isRecurringMaster;

    setLoading(true);
    setDeleteConfirmOpen(false);
    
    try {
      if (deleteScope === 'single' || !isRecurring) {
        // Delete single event
        const { error } = await (supabase as any)
          .from('events')
          .delete()
          .eq('id', event.id);
        if (error) throw error;
      } else if (deleteScope === 'future') {
        // Delete future events via edge function
        const { error } = await (supabase as any).functions.invoke('manage-event', {
          body: {
            action: 'delete_future_series',
            parent_id: event.parentEventId,
            from_date: event.date.toISOString().split('T')[0]
          }
        });
        if (error) throw error;
      } else if (deleteScope === 'all') {
        // Delete entire series
        const { error } = await (supabase as any).functions.invoke('manage-event', {
          body: {
            action: 'delete_entire_series',
            parent_id: event.parentEventId || event.id
          }
        });
        if (error) throw error;
      }

      toast.success(t('admin.event_deleted'));
      onOpenChange(false);
      if (onEventDeleted) {
        onEventDeleted();
      } else {
        onEventUpdated();
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!event) return;
    exportParticipantsToCsv(event);
    toast.success('Participant list exported');
  };

  const handleCancelEvent = (scope: 'single' | 'future') => {
    setCancelScope(scope);
    setCancelDialogOpen(true);
  };

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{t('admin.edit_event')}</DialogTitle>
            <div className="flex gap-2 mr-8">
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDuplicateDialogOpen(true)}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </Button>
            </div>
          </div>
          
          {/* Recurring Event Info Section */}
          {(event.parentEventId || masterEvent) && (
            <Alert className="mt-4">
              <Repeat className="h-4 w-4" />
              <AlertTitle>{t('admin.recurring_info')}</AlertTitle>
              <AlertDescription>
                <div className="space-y-1 text-sm">
                  {masterEvent?.recurrence_pattern && (
                    <div>
                      <strong>Pattern:</strong> {t(`admin.recurrence.${masterEvent.recurrence_pattern}`)}
                    </div>
                  )}
                  {masterEvent?.recurrence_end_date && (
                    <div>
                      <strong>{t('admin.repeats_until')}:</strong> {format(new Date(masterEvent.recurrence_end_date), 'PPP')}
                    </div>
                  )}
                  {event.parentEventId && (
                    <Button 
                      variant="link" 
                      className="h-auto p-0 text-sm"
                      onClick={() => setSeriesManagerOpen(true)}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      {t('admin.view_full_series')} →
                    </Button>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label>{t('event.title')}</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>{t('event.description')}</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('event.date')}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !formData.eventDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.eventDate ? format(formData.eventDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.eventDate}
                    onSelect={(date) => date && setFormData({ ...formData, eventDate: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>{t('event.time')}</Label>
              <Input
                type="time"
                value={formData.eventTime}
                onChange={(e) => setFormData({ ...formData, eventTime: e.target.value })}
              />
            </div>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label>{t('event.location')}</Label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            />
          </div>

          {/* Group/Subgroup */}
          <div className="space-y-2">
            <Label>{t('group.select')}</Label>
            <Select value={formData.groupId} onValueChange={(value) => setFormData({ ...formData, groupId: value })}>
              <SelectTrigger className="bg-background">
                <SelectValue placeholder={t('group.select')} />
              </SelectTrigger>
              <SelectContent className="bg-popover z-50">
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Level and Max Participants */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('event.level')}</Label>
              <Input
                value={formData.level}
                onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                placeholder="Beginner, Intermediate, Advanced"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('event.max_participants')}</Label>
              <Input
                type="number"
                min="1"
                value={formData.maxParticipants}
                onChange={(e) => {
                  console.log('[EditEventDialog] Max participants input changed:', e.target.value);
                  const rawValue = e.target.value;
                  
                  // Allow empty input for user to clear/type
                  if (rawValue === '') {
                    console.log('[EditEventDialog] Empty input, not updating state yet');
                    return;
                  }
                  
                  const val = parseInt(rawValue, 10);
                  console.log('[EditEventDialog] Parsed value:', val);
                  
                  // Update state with any valid number (including negative during typing)
                  if (!isNaN(val)) {
                    console.log('[EditEventDialog] Updating maxParticipants to:', val);
                    setFormData(prev => {
                      console.log('[EditEventDialog] Previous maxParticipants:', prev.maxParticipants);
                      return { ...prev, maxParticipants: val };
                    });
                  }
                }}
              />
            </div>
          </div>

          {/* Custom Signup Window */}
          <div className="space-y-2">
            <Label>{t('admin.custom_signup_window')}</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={formData.customSignupHours || ''}
                placeholder={t('admin.use_default_signup')}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  customSignupHours: e.target.value ? parseInt(e.target.value) : undefined 
                })}
              />
              <span className="text-sm text-muted-foreground">hours</span>
            </div>
            {formData.customSignupHours && (
              <p className="text-sm text-muted-foreground">
                {t('event.custom_signup_window', { hours: formData.customSignupHours })}
              </p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>{t('event.notes')}</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <div className="flex justify-between w-full">
            <div className="flex gap-2">
              {/* Cancel Event Dropdown */}
              {event.parentEventId ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={loading}>
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancel Event
                      <ChevronDown className="h-4 w-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={() => handleCancelEvent('single')}>
                      Cancel only this occurrence
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleCancelEvent('future')}>
                      Cancel this and future occurrences
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleCancelEvent('single')}
                  disabled={loading}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel Event
                </Button>
              )}

              {/* Delete Button */}
              {event.parentEventId || event.isRecurringMaster ? (
                <Button 
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete()}
                  disabled={loading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('admin.delete_event')}
                </Button>
              ) : (
                <Button 
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete('single')}
                  disabled={loading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('admin.delete_event')}
                </Button>
              )}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={loading}>
                {loading ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Recurring Event Manager */}
      {event?.parentEventId && (
        <RecurringEventManager
          open={seriesManagerOpen}
          onOpenChange={setSeriesManagerOpen}
          seriesId={event.parentEventId}
          onSeriesUpdated={() => {
            onEventUpdated();
            setSeriesManagerOpen(false);
          }}
        />
      )}

      {/* Duplicate Event Dialog */}
      <DuplicateEventDialog
        open={duplicateDialogOpen}
        onOpenChange={setDuplicateDialogOpen}
        event={event}
        onDuplicated={() => {
          onEventUpdated();
          setDuplicateDialogOpen(false);
        }}
      />

      {/* Cancel Event Dialog */}
      <CancelEventDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        event={event}
        scope={cancelScope}
        onCancelled={() => {
          onEventUpdated();
          setCancelDialogOpen(false);
        }}
      />

      {/* Edit Scope Confirmation Dialog */}
      <Dialog open={editScopeDialogOpen} onOpenChange={(open) => {
        setEditScopeDialogOpen(open);
        if (!open) setPendingSave(false);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.edit_recurring_prompt')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors">
                <input
                  type="radio"
                  name="editScope"
                  value="single"
                  checked={formData.editScope === 'single'}
                  onChange={(e) => setFormData({ ...formData, editScope: e.target.value as 'single' | 'future' })}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="font-medium">{t('admin.edit_scope.single')}</div>
                  <div className="text-sm text-muted-foreground">
                    {t('admin.edit_scope.single_description')}
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors">
                <input
                  type="radio"
                  name="editScope"
                  value="future"
                  checked={formData.editScope === 'future'}
                  onChange={(e) => setFormData({ ...formData, editScope: e.target.value as 'single' | 'future' })}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="font-medium">{t('admin.edit_scope.future')}</div>
                  <div className="text-sm text-muted-foreground">
                    {t('admin.edit_scope.future_description')}
                  </div>
                </div>
              </label>
            </div>
          </div>
          <DialogFooter className="flex-row gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setEditScopeDialogOpen(false);
                setPendingSave(false);
              }}
              disabled={loading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={async () => {
                setEditScopeDialogOpen(false);
                await performSave(formData.editScope);
              }}
              disabled={loading}
            >
              {loading ? t('common.saving') : t('admin.edit_scope.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.confirm_delete')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {(event?.parentEventId || event?.isRecurringMaster) ? (
              <>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('admin.delete_recurring_prompt')}
                </p>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors">
                    <input
                      type="radio"
                      name="deleteScope"
                      value="single"
                      checked={deleteScope === 'single'}
                      onChange={(e) => setDeleteScope(e.target.value as any)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{t('admin.delete_scope.single')}</div>
                      <div className="text-sm text-muted-foreground">
                        {t('admin.delete_confirm.single')}
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors">
                    <input
                      type="radio"
                      name="deleteScope"
                      value="future"
                      checked={deleteScope === 'future'}
                      onChange={(e) => setDeleteScope(e.target.value as any)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{t('admin.delete_scope.future')}</div>
                      <div className="text-sm text-muted-foreground">
                        {t('admin.delete_confirm.future')}
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors">
                    <input
                      type="radio"
                      name="deleteScope"
                      value="all"
                      checked={deleteScope === 'all'}
                      onChange={(e) => setDeleteScope(e.target.value as any)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{t('admin.delete_scope.all')}</div>
                      <div className="text-sm text-muted-foreground">
                        {t('admin.delete_confirm.all')}
                      </div>
                    </div>
                  </label>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('admin.delete_confirm.single_standalone')}
              </p>
            )}
          </div>
          <DialogFooter className="flex-row gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={loading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={loading}
            >
              {loading ? 'Deleting...' : t('admin.delete_event')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
